// Help popup for the file-upload WebUI.
//
// Plain read-only content describing the three supported upload
// formats and the required sections / fields. No calculation logic
// and no parser logic — this is pure documentation surfaced from a
// small modal trigger.

import React, { useState } from "react";
import { Button, Drawer, Space, Typography, Divider, Tag } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

export function UploadFormatHelp(): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="link"
        size="small"
        icon={<QuestionCircleOutlined />}
        onClick={() => setOpen(true)}
        style={{ paddingInline: 0 }}
      >
        查看上传格式要求
      </Button>
      <Drawer
        title="上传文件格式要求"
        placement="right"
        width={Math.min(640, typeof window !== "undefined" ? window.innerWidth - 40 : 640)}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <section>
            <Title level={5} style={{ marginTop: 0 }}>
              支持的文件格式
            </Title>
            <Paragraph>
              <Tag color="blue">.json</Tag>
              <Tag color="blue">.csv</Tag>
              <Tag color="blue">.xlsx</Tag>
              三个文件扩展名。每个文件在浏览器本地解析后直接调用核心引擎，不会上传到任何服务器。
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              建议先从 <Text code>examples/</Text> 目录复制对应模板再修改，避免字段拼写错误。
            </Paragraph>
          </section>

          <Divider style={{ margin: 0 }} />

          <section>
            <Title level={5} style={{ marginTop: 0 }}>
              JSON 顶层结构
            </Title>
            <Paragraph>
              所有 JSON 输入均为同一个形状，<Text code>calculationType</Text> 决定引擎走哪条规则路径：
            </Paragraph>
            <pre
              style={{
                background: "#fafafa",
                padding: 12,
                borderRadius: 4,
                fontSize: 12,
                overflow: "auto",
              }}
            >
{`{
  "calculationType": "new-supply",
  "input": {
    "parcel":  { ... },
    "prices":  { ... },
    "items":   [ ... ]
  }
}`}
            </pre>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              其它合法 <Text code>calculationType</Text>：change-premium / excess-gfa / temporary-use /
              short-term-lease / lease-rent / capacity-increase / batch。详细示例见 <Text code>examples/*.sample.json</Text>。
            </Paragraph>
          </section>

          <Divider style={{ margin: 0 }} />

          <section>
            <Title level={5} style={{ marginTop: 0 }}>
              CSV / xlsx 必填段落
            </Title>
            <Paragraph>
              CSV 与 xlsx <b>当前仅支持 new-supply</b>。文件必须按以下顺序包含三个段落：
            </Paragraph>
            <ul>
              <li><Text code>[PARCEL]</Text> 或 <Text code>PARCEL</Text> 工作表 — <Text code>key,value</Text> 两列</li>
              <li><Text code>[PRICES]</Text> 或 <Text code>PRICES</Text> 工作表 — <Text code>key,value</Text> 两列</li>
              <li><Text code>[ITEMS]</Text> 或 <Text code>ITEMS</Text> 工作表 — 首行字段名表头，其后每行一个分项</li>
            </ul>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              参考模板：<Text code>examples/new-supply.template.csv</Text> 与 <Text code>examples/new-supply.template.xlsx</Text>。
            </Paragraph>
          </section>

          <Divider style={{ margin: 0 }} />

          <section>
            <Title level={5} style={{ marginTop: 0 }}>
              [PARCEL] 字段
            </Title>
            <Paragraph>必填字段：</Paragraph>
            <ul>
              <li><Text code>projectName</Text> — 项目名称</li>
              <li><Text code>calculationDate</Text> — 测算时点（字符串，如 <Text code>2026-06-17</Text>）</li>
              <li><Text code>landArea</Text> — 土地面积（㎡），&gt; 0</li>
              <li><Text code>totalGfa</Text> — 总建筑面积（㎡），&gt; 0</li>
              <li><Text code>supplyMethod</Text> — 招拍挂 / 协议 / 租赁 / 作价出资 / 划拨转有偿 / 其他</li>
              <li><Text code>calculationType</Text> — 新供应土地 / 变更补缴 / 竣工超面积 / 产业用地提高容积 / 租赁用地年租金 / 临时用地使用费 / 留用土地 / 城市更新 / 特殊情形</li>
              <li><Text code>mainLandUse</Text> — 住宅 / 商业 / 办公 / 工业 / 新型产业 / 酒店 / 公共管理与服务设施 / 公用设施</li>
              <li><Text code>projectType</Text> — 普通项目 / 产业项目 / 重点产业项目 / 总部项目 / 棚户区改造 / 留用土地 / 城市更新 / 历史遗留 / 其他</li>
            </ul>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              可选：<Text code>parcelId</Text>、<Text code>governmentOwnedGfa</Text>、<Text code>remark</Text>。
            </Paragraph>
          </section>

          <Divider style={{ margin: 0 }} />

          <section>
            <Title level={5} style={{ marginTop: 0 }}>
              [PRICES] 字段
            </Title>
            <Paragraph>
              <Text code>key</Text> 为适用地价类型，<Text code>value</Text> 为元/㎡ 的正数。至少填入测算会用到的类型：
            </Paragraph>
            <ul>
              <li><Text code>residential</Text> — 住宅</li>
              <li><Text code>commercial</Text> — 商业</li>
              <li><Text code>office</Text> — 办公</li>
              <li><Text code>industrial</Text> — 工业</li>
              <li><Text code>m0</Text> — 新型产业</li>
              <li><Text code>hotel</Text> — 酒店</li>
              <li><Text code>publicService</Text> — 公共管理与服务设施</li>
              <li><Text code>utility</Text> — 公用设施</li>
            </ul>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              引擎不会自动生成市场价格，必须由用户录入或外部数据导入。
            </Paragraph>
          </section>

          <Divider style={{ margin: 0 }} />

          <section>
            <Title level={5} style={{ marginTop: 0 }}>
              [ITEMS] 字段
            </Title>
            <Paragraph>每个分项必填：</Paragraph>
            <ul>
              <li><Text code>id</Text> — 分项 ID，非空字符串</li>
              <li><Text code>buildingType</Text> — 建筑类型（详见 <Text code>src/types/enums.ts</Text>）</li>
              <li><Text code>gfa</Text> — 建筑面积（㎡），&gt; 0</li>
              <li><Text code>landTermYears</Text> — 剩余年期，&gt; 0</li>
              <li><Text code>propertyCondition</Text> — 不得转让 / 限整体转让 / 允许分割转让</li>
              <li><Text code>isGovernmentOwned</Text> — <Text code>true</Text> / <Text code>false</Text></li>
              <li><Text code>isUnderground</Text> — <Text code>true</Text> / <Text code>false</Text></li>
            </ul>
            <Paragraph>条件字段：</Paragraph>
            <ul>
              <li><Text code>commercialFloor</Text> — 商业分项建议填写（首层商业 / 第二层商业 / 第三层商业 / 第四层及以上商业）</li>
              <li><Text code>undergroundType</Text> — 当 <Text code>isUnderground=true</Text> 时必填（交通设施或综合管廊 / 其他地下空间）</li>
              <li><Text code>affordableHousingType</Text> — 当分项为保障性住房时必填（公共租赁住房 / 保障性租赁住房-政府组织配租 / 保障性租赁住房-社会主体出租 / 配售型保障性住房）</li>
              <li><Text code>industryProjectType</Text> — 产业用地时建议填写（重点产业项目-联合申请 / 重点产业项目-单一意向单位 / 一般产业项目 / 总部项目）</li>
            </ul>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              可选：<Text code>applicablePriceType</Text>、<Text code>isAffordableHousing</Text>、
              <Text code>isIndustryOriented</Text>、<Text code>isSupportingFacility</Text>、
              <Text code>supportingFacilityType</Text>、<Text code>isCapacityIncrease</Text>、<Text code>remark</Text>。
            </Paragraph>
          </section>

          <Divider style={{ margin: 0 }} />

          <section>
            <Title level={5} style={{ marginTop: 0 }}>
              市场价来源元数据（全部可选）
            </Title>
            <Paragraph>
              属于记录性字段，写在 <Text code>[PRICES]</Text> 段落或 JSON 的 <Text code>prices</Text> 对象里。
              引擎不参与测算，但会原样写入 Markdown / xlsx 报告的「市场价格来源说明」段落：
            </Paragraph>
            <ul>
              <li><Text code>marketPriceSource</Text> — 来源标签（如「深圳市自然资源局」）</li>
              <li><Text code>marketPriceYear</Text> — 发布年份，4 位字符串（如 <Text code>2026</Text>）</li>
              <li><Text code>valuationDate</Text> — 评估日期，ISO 8601（<Text code>YYYY-MM-DD</Text>）</li>
              <li><Text code>effectiveDate</Text> — 生效日期，ISO 8601（<Text code>YYYY-MM-DD</Text>）</li>
              <li><Text code>standardParcelCode</Text> — 标准宗地号</li>
              <li><Text code>benchmarkZoneCode</Text> — 区段编号</li>
              <li><Text code>officialQueryUrl</Text> — 官方查询 URL（纯字符串，不抓取）</li>
              <li><Text code>priceSourceRemark</Text> — 备注</li>
            </ul>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              项目本身不抓取 / 不调用官方查询网站；以上字段仅作为录入与审计记录。
            </Paragraph>
          </section>

          <Divider style={{ margin: 0 }} />

          <section>
            <Title level={5} style={{ marginTop: 0 }}>
              模板与示例
            </Title>
            <Paragraph>
              复制以下文件作为起点：
            </Paragraph>
            <ul>
              <li><Text code>examples/new-supply.sample.json</Text> — JSON 标准示例</li>
              <li><Text code>examples/new-supply.template.csv</Text> — CSV 模板</li>
              <li><Text code>examples/new-supply.template.xlsx</Text> — xlsx 模板</li>
              <li><Text code>examples/csv-template.md</Text> — CSV 字段参考</li>
            </ul>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              校验失败时，结果页的「校验消息」区域会逐条列出字段路径与建议，按提示修改后再次上传即可。
            </Paragraph>
          </section>
        </Space>
      </Drawer>
    </>
  );
}
